/**
 * Error Transformation Utilities
 *
 * Provides unified error handling and transformation for API errors.
 * Converts Axios errors, network errors, and other error types into
 * a consistent format with user-friendly messages.
 */

import type { AxiosError } from "axios";

/**
 * Unified API Error Format
 * This is the standard error format used throughout the application
 */
export interface ApiError extends Error {
	// Standard error fields
	name: string;
	message: string;

	// API-specific fields
	code: string;
	status?: number;
	statusText?: string;
	data?: unknown;

	// User-facing fields
	userMessage: string;
	technicalDetails?: string;

	// Metadata
	timestamp: string;
	correlationId?: string;

	// Retry information
	canRetry: boolean;
	retryAfter?: number;

	// Original error
	originalError?: Error;
}

/**
 * Error code constants
 */
export const ERROR_CODES = {
	// Network errors
	NETWORK_ERROR: "NETWORK_ERROR",
	TIMEOUT_ERROR: "TIMEOUT_ERROR",
	ABORT_ERROR: "ABORT_ERROR",

	// HTTP errors
	BAD_REQUEST: "BAD_REQUEST",
	UNAUTHORIZED: "UNAUTHORIZED",
	FORBIDDEN: "FORBIDDEN",
	NOT_FOUND: "NOT_FOUND",
	CONFLICT: "CONFLICT",
	RATE_LIMITED: "RATE_LIMITED",
	SERVER_ERROR: "SERVER_ERROR",
	SERVICE_UNAVAILABLE: "SERVICE_UNAVAILABLE",

	// Validation errors
	VALIDATION_ERROR: "VALIDATION_ERROR",
	SCHEMA_VALIDATION_ERROR: "SCHEMA_VALIDATION_ERROR",

	// Unknown
	UNKNOWN_ERROR: "UNKNOWN_ERROR",
} as const;

/**
 * User-friendly error messages by code
 */
const USER_MESSAGES: Record<string, string> = {
	[ERROR_CODES.NETWORK_ERROR]:
		"Unable to connect to the server. Please check your internet connection.",
	[ERROR_CODES.TIMEOUT_ERROR]:
		"The request took too long to complete. Please try again.",
	[ERROR_CODES.ABORT_ERROR]: "The request was cancelled.",
	[ERROR_CODES.BAD_REQUEST]:
		"Invalid request. Please check your input and try again.",
	[ERROR_CODES.UNAUTHORIZED]:
		"You need to log in to access this resource. Redirecting to login...",
	[ERROR_CODES.FORBIDDEN]: "You don't have permission to access this resource.",
	[ERROR_CODES.NOT_FOUND]: "The requested resource could not be found.",
	[ERROR_CODES.CONFLICT]:
		"This action conflicts with existing data. Please refresh and try again.",
	[ERROR_CODES.RATE_LIMITED]:
		"Too many requests. Please wait a moment before trying again.",
	[ERROR_CODES.SERVER_ERROR]:
		"A server error occurred. Our team has been notified.",
	[ERROR_CODES.SERVICE_UNAVAILABLE]:
		"The service is temporarily unavailable. Please try again later.",
	[ERROR_CODES.VALIDATION_ERROR]:
		"Please check your input and correct any errors.",
	[ERROR_CODES.SCHEMA_VALIDATION_ERROR]:
		"The data received from the server is in an unexpected format.",
	[ERROR_CODES.UNKNOWN_ERROR]:
		"An unexpected error occurred. Please try again.",
};

/**
 * Determine if an error is retryable based on status code
 */
function isRetryable(status?: number): boolean {
	if (!status) return false;

	// Retry on server errors and rate limiting
	return (
		status === 429 || // Rate limited
		status === 500 || // Internal server error
		status === 502 || // Bad gateway
		status === 503 || // Service unavailable
		status === 504 // Gateway timeout
	);
}

/**
 * Get retry-after value from response headers
 */
function getRetryAfter(error: AxiosError): number | undefined {
	const retryAfter = error.response?.headers?.["retry-after"];
	if (!retryAfter) return undefined;

	// Try parsing as seconds
	const seconds = Number.parseInt(retryAfter, 10);
	if (!Number.isNaN(seconds)) {
		return seconds * 1000; // Convert to milliseconds
	}

	// Try parsing as HTTP date
	const date = new Date(retryAfter);
	if (!Number.isNaN(date.getTime())) {
		return Math.max(0, date.getTime() - Date.now());
	}

	return undefined;
}

/**
 * Extract error code from Axios error
 */
function getErrorCode(error: AxiosError): string {
	// Network errors
	if (error.code === "ECONNABORTED" || error.message.includes("timeout")) {
		return ERROR_CODES.TIMEOUT_ERROR;
	}
	if (error.code === "ERR_NETWORK" || error.message.includes("Network Error")) {
		return ERROR_CODES.NETWORK_ERROR;
	}
	if (error.code === "ERR_CANCELED") {
		return ERROR_CODES.ABORT_ERROR;
	}

	// HTTP status codes
	const status = error.response?.status;
	switch (status) {
		case 400:
			return ERROR_CODES.BAD_REQUEST;
		case 401:
			return ERROR_CODES.UNAUTHORIZED;
		case 403:
			return ERROR_CODES.FORBIDDEN;
		case 404:
			return ERROR_CODES.NOT_FOUND;
		case 409:
			return ERROR_CODES.CONFLICT;
		case 429:
			return ERROR_CODES.RATE_LIMITED;
		case 500:
			return ERROR_CODES.SERVER_ERROR;
		case 503:
			return ERROR_CODES.SERVICE_UNAVAILABLE;
		default:
			if (status && status >= 500) {
				return ERROR_CODES.SERVER_ERROR;
			}
			return ERROR_CODES.UNKNOWN_ERROR;
	}
}

/**
 * Extract error message from response data
 */
function extractErrorMessage(data: unknown): string | undefined {
	if (!data || typeof data !== "object") return undefined;

	// Try common message fields
	const errorData = data as Record<string, unknown>;

	return (
		(errorData.message as string) ||
		(errorData.error as string) ||
		(errorData.detail as string) ||
		(errorData.title as string) ||
		undefined
	);
}

/**
 * Transform Axios error into unified ApiError format
 */
export function transformAxiosError(error: AxiosError): ApiError {
	const code = getErrorCode(error);
	const status = error.response?.status;
	const statusText = error.response?.statusText;
	const data = error.response?.data;

	// Extract technical message
	const technicalMessage =
		extractErrorMessage(data) || error.message || "Unknown error";

	// Get user-friendly message
	const userMessage =
		USER_MESSAGES[code] || USER_MESSAGES[ERROR_CODES.UNKNOWN_ERROR];

	// Create ApiError
	const apiError: ApiError = Object.assign(new Error(technicalMessage), {
		name: "ApiError",
		message: technicalMessage,
		code,
		status,
		statusText,
		data,
		userMessage,
		technicalDetails: error.config?.url
			? `${error.config.method?.toUpperCase()} ${error.config.url}`
			: undefined,
		timestamp: new Date().toISOString(),
		correlationId: error.config?.headers?.["X-Correlation-ID"] as
			| string
			| undefined,
		canRetry: isRetryable(status),
		retryAfter: getRetryAfter(error),
		originalError: error,
	});

	return apiError;
}

/**
 * Transform any error into ApiError format
 */
export function transformError(error: unknown): ApiError {
	// Already an ApiError
	if (isApiError(error)) {
		return error;
	}

	// Axios error
	if (isAxiosError(error)) {
		return transformAxiosError(error);
	}

	// Standard Error
	if (error instanceof Error) {
		return Object.assign(new Error(error.message), {
			name: "ApiError",
			message: error.message,
			code: ERROR_CODES.UNKNOWN_ERROR,
			userMessage: USER_MESSAGES[ERROR_CODES.UNKNOWN_ERROR],
			technicalDetails: error.stack,
			timestamp: new Date().toISOString(),
			canRetry: false,
			originalError: error,
		});
	}

	// Unknown error type
	const message = String(error);
	return Object.assign(new Error(message), {
		name: "ApiError",
		message,
		code: ERROR_CODES.UNKNOWN_ERROR,
		userMessage: USER_MESSAGES[ERROR_CODES.UNKNOWN_ERROR],
		timestamp: new Date().toISOString(),
		canRetry: false,
	});
}

/**
 * Type guard for ApiError
 */
export function isApiError(error: unknown): error is ApiError {
	return (
		error instanceof Error &&
		"code" in error &&
		"userMessage" in error &&
		"canRetry" in error
	);
}

/**
 * Type guard for AxiosError
 */
export function isAxiosError(error: unknown): error is AxiosError {
	return (
		typeof error === "object" &&
		error !== null &&
		"isAxiosError" in error &&
		error.isAxiosError === true
	);
}

/**
 * Create a custom ApiError manually
 */
export function createApiError(
	message: string,
	code: string,
	options?: {
		status?: number;
		data?: unknown;
		userMessage?: string;
		canRetry?: boolean;
		originalError?: Error;
	},
): ApiError {
	return Object.assign(new Error(message), {
		name: "ApiError",
		message,
		code,
		status: options?.status,
		data: options?.data,
		userMessage: options?.userMessage || USER_MESSAGES[code] || message,
		timestamp: new Date().toISOString(),
		canRetry: options?.canRetry ?? false,
		originalError: options?.originalError,
	});
}

/**
 * Log error to console (respects debug mode)
 */
export function logError(error: ApiError, debug = false): void {
	if (!debug && import.meta.env.PROD) return;

	console.error("❌ API Error:", {
		code: error.code,
		message: error.message,
		userMessage: error.userMessage,
		status: error.status,
		correlationId: error.correlationId,
		timestamp: error.timestamp,
		canRetry: error.canRetry,
		retryAfter: error.retryAfter,
		data: error.data,
	});

	if (error.originalError && import.meta.env.DEV) {
		console.error("Original Error:", error.originalError);
	}
}

/**
 * Format error for display to user
 */
export function formatErrorForDisplay(error: unknown): {
	title: string;
	message: string;
	canRetry: boolean;
} {
	const apiError = isApiError(error) ? error : transformError(error);

	return {
		title: getErrorTitle(apiError.code),
		message: apiError.userMessage,
		canRetry: apiError.canRetry,
	};
}

/**
 * Get error title by code
 */
function getErrorTitle(code: string): string {
	const titles: Record<string, string> = {
		[ERROR_CODES.NETWORK_ERROR]: "Connection Error",
		[ERROR_CODES.TIMEOUT_ERROR]: "Request Timeout",
		[ERROR_CODES.UNAUTHORIZED]: "Authentication Required",
		[ERROR_CODES.FORBIDDEN]: "Access Denied",
		[ERROR_CODES.NOT_FOUND]: "Not Found",
		[ERROR_CODES.RATE_LIMITED]: "Too Many Requests",
		[ERROR_CODES.SERVER_ERROR]: "Server Error",
		[ERROR_CODES.SERVICE_UNAVAILABLE]: "Service Unavailable",
		[ERROR_CODES.VALIDATION_ERROR]: "Validation Error",
	};

	return titles[code] || "Error";
}
